import { Link, Skeleton } from '@purpur/library';
import { purpurBreakpointMd } from '@purpur/library/tokens';
import styled, { css } from 'styled-components';

import { NAVIGATION_MODAL_ZINDEX } from '@telialt/common-constants/src/modal';
import {
    getPurpurBorder,
    getPurpurColor,
    getPurpurSpacing,
    getPurpurTypography,
    purpurMedia,
} from '@telialt/common-theme/src/purpurSelectors';
import { NavigationItemDisplayOption } from '@telialt/models/src/Navigation';

import { MenuItemIcon, StyledMenuComponentBase, StyledMenuItem } from '../MenuItem/MenuItem.styles';
import { MobileMenuItemContent } from '../MobileMenu/MobileMenu.styles';
import { displayOption } from '../Navigation.styles';

export const UserButtonSkeletonWrapper = styled.div<{ $displayOption: NavigationItemDisplayOption }>`
    ${({ $displayOption }) => displayOption($displayOption)};
    width: 3.13rem;
    height: 4rem;
    align-items: center;
    align-self: center;
    justify-content: center;
    gap: ${getPurpurSpacing('50')};
    flex-direction: column;

    ${purpurMedia('md')`
        width: 5.5rem;
        flex-direction: row;
        height: 1.5rem;
    `};
`;

export const UserButtonSkeleton = styled(Skeleton)`
    width: 100%;
    height: 1.2rem;

    &:first-of-type {
        height: 1.8rem;
    }

    ${purpurMedia('md')`
        height: 100%;

        &:first-of-type {
            height: 100%;
            width: 2rem;
        }
    `};
`;

export const UserIconWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`;

const userButtonStyles = css`
    width: 5.5rem;

    @media screen and (max-width: calc(${purpurBreakpointMd} - 1px)) {
        flex-direction: column;
        justify-content: center;
        gap: 0;
        font-size: ${getPurpurTypography('scale75')};
        width: 3.65rem;

        ${MenuItemIcon} {
            width: ${getPurpurSpacing('300')};
            height: ${getPurpurSpacing('300')};
            margin-top: 0.375rem;
            margin-bottom: 0.375rem;
        }
    }
`;

export const GuestButton = styled(StyledMenuItem)`
    ${userButtonStyles};
    width: auto;

    @media screen and (max-width: calc(${purpurBreakpointMd} - 1px)) {
        width: auto;
    }
`;

export const UserButton = styled(StyledMenuComponentBase).attrs({ as: 'button', type: 'button' })`
    ${userButtonStyles};
    ${({ $isActive }) =>
        $isActive &&
        css`
            color: ${getPurpurColor('textInteractiveSelected')};
        `}
`;

export const UserMenuList = styled.div`
    z-index: ${NAVIGATION_MODAL_ZINDEX};
    filter: drop-shadow(0 ${getPurpurSpacing('50')} ${getPurpurSpacing('150')} rgba(0, 0, 0, 0.1));
    border-radius: ${getPurpurBorder('radiusSm')};
    width: 17.5rem;
    margin-top: ${getPurpurSpacing('50')};
`;

export const ListItem = styled.div`
    background: ${getPurpurColor('functionalWhite')};
    display: flex;
    align-items: center;

    & + & {
        border-top: ${getPurpurBorder('widthXs')} solid ${getPurpurColor('gray100')};
    }
`;

export const MenuListItemLink = styled(Link)`
    width: 100%;

    a {
        gap: ${getPurpurSpacing('200')};
        font-weight: ${getPurpurTypography('weightMedium')};
        text-decoration: none;
        width: 100%;
        padding: 0 ${getPurpurSpacing('300')};
        height: 4rem;
    }
`;

export const UserMenuContainer = styled.div`
    position: relative;
    height: 100%;
    display: flex;
    align-items: center;

    ${purpurMedia('lg')`
        display: none;
    `};
`;

export const MainMenuContainer = styled.div`
    flex: 1;
    overflow: hidden;
`;

export const StyledMobileMenuItemContent = styled(MobileMenuItemContent)`
    gap: ${getPurpurSpacing('150')};
`;
